import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import ReactHintFactory from 'react-hint';
import { useEditor, Editor, EditorContent } from '@tiptap/react';
import { Bold } from '@tiptap/extension-bold';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { Document } from '@tiptap/extension-document';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Underline } from '@tiptap/extension-underline';
import { CharacterCount, Placeholder, UndoRedo } from '@tiptap/extensions';
import { useReduxState } from 'hooks';
import {
  CodeExtensionWithKeyboardShortcuts,
  RichTextEditorMenu,
  RichTextEditorTemplateTags
} from '.';

import type { LinkOptions } from '@tiptap/extension-link';
import type { EmailMessagePlaceholder } from 'shared-types';

const ReactHint = ReactHintFactory(React);

const SimpleDocument = Document.extend({
  content: 'block'
});

type RichTextEditorInputProps = {
  autoGrow?: boolean;
  colorPickerColors?: string[];
  disabled?: boolean;
  error?: string;
  formatting?: boolean;
  getEditor?: (editor: Editor) => void;
  maxLength?: number;
  maxLengthShowError?: boolean;
  maxLengthShowInfo?: boolean;
  onFocus?: (editor: Editor) => void;
  /**
   * Receives the editor value.
   * Returns plain text if `formatting` is disabled, otherwise HTML.
   */
  onChange?: (value: string) => void;
  linkOptions?: Partial<LinkOptions>;
  placeholder?: string;
  rows?: number;
  singleLine?: boolean;
  singleLineRemoveParagraph?: boolean;
  value?: string;
  valueUpdateTimeout?: number;
  withBorder?: boolean;
  wrapperStyles?: React.CSSProperties;
} & (
  | {
      templateTags: { key: string; label: string; value?: string }[];
      templateTagsIdentifier: string;
    }
  | {
      templateTags?: never;
      templateTagsIdentifier?: never;
    }
);

const RichTextEditorInput = ({
  autoGrow = true,
  colorPickerColors,
  disabled = false,
  error,
  formatting = true,
  getEditor,
  maxLength,
  maxLengthShowError = false,
  maxLengthShowInfo = true,
  onFocus,
  onChange,
  linkOptions,
  placeholder,
  rows = 5,
  singleLine,
  singleLineRemoveParagraph,
  templateTags,
  templateTagsIdentifier,
  value,
  valueUpdateTimeout = 500,
  withBorder = true,
  wrapperStyles
}: RichTextEditorInputProps) => {
  const { messages, theme } = useReduxState();

  const lastEmittedRef = useRef<string | null>(null);
  const richTextElRef = useRef<HTMLDivElement>(undefined);
  const updateValueTimeout = useRef<NodeJS.Timeout>(undefined);

  const minHeight = 18 + rows * 19;

  let editorClassName = 'vuo-rich-text-editor';
  if (!singleLine) editorClassName += ' vuo-rich-text-editor-multiline';
  if (!autoGrow) editorClassName += ' vuo-rich-text-editor-fixed';
  if (disabled) editorClassName += ' vuo-rich-text-editor-disabled';
  if (error) editorClassName += ' vuo-rich-text-editor-error';
  if (withBorder) editorClassName += ' vuo-rich-text-editor-with-border';

  const formattingExtensions = [
    ...(formatting
      ? [
          Bold,
          CodeExtensionWithKeyboardShortcuts,
          Italic,
          Link.configure({
            openOnClick: false,
            ...(linkOptions ?? {})
          }),
          Underline
        ]
      : []),
    ...(colorPickerColors?.length ? [Color, TextStyle] : [])
  ];

  const processValue = (value: string) => {
    if (value === '<p></p>') {
      value = '';
    }
    if (singleLineRemoveParagraph) {
      value = value.replace('<p>', '').replace('</p>', '');
    }
    return value;
  };

  const getEditorValue = (editor: Editor) =>
    formatting
      ? processValue(editor.getHTML())
      : singleLine
      ? editor.getText().trim()
      : editor.getText();

  const normalizeValue = (value: string) =>
    formatting ? processValue(value) : singleLine ? value.trim() : value;

  const editor = useEditor({
    editable: !disabled,
    editorProps: { attributes: { class: editorClassName } },
    extensions: [
      CharacterCount.configure({
        limit: maxLength || (singleLine ? 220 : 5000)
      }),
      Placeholder.configure({
        placeholder: placeholder || messages.commonAddText,
        showOnlyWhenEditable: false
      }),
      Text,
      UndoRedo,
      Paragraph,
      ...formattingExtensions,
      ...(!singleLine ? [Document, HardBreak] : [SimpleDocument]),
      ...(templateTags?.length
        ? [
            RichTextEditorTemplateTags.configure({
              availableTags: templateTags,
              identifier: templateTagsIdentifier
            })
          ]
        : [])
    ],
    content: formatting ? value : toPlainTextContent(value),
    immediatelyRender: false,
    onFocus({ editor }) {
      if (onFocus) onFocus(editor);
    },
    onUpdate({ editor }) {
      const currentValue = getEditorValue(editor);
      const normalizedValue = normalizeValue(value ?? '');
      if (currentValue === normalizedValue) return;

      if (onChange) {
        clearTimeout(updateValueTimeout.current);
        updateValueTimeout.current = setTimeout(() => {
          lastEmittedRef.current = currentValue;
          onChange(currentValue);
        }, valueUpdateTimeout);
      }
    }
  });

  useEffect(() => {
    if (editor && getEditor) getEditor(editor);
  }, [editor, getEditor]);

  useEffect(() => {
    // editor options do not update dynamically, so we handle
    // changes to the disabled prop manually
    if (editor && editor.isEditable === Boolean(disabled)) {
      editor.setEditable(!disabled);
      editor.setOptions({
        editorProps: { attributes: { class: editorClassName } }
      });
    }
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;
    if (value === undefined) return;

    const normalizedValue = normalizeValue(value);

    if (normalizedValue === lastEmittedRef.current) {
      lastEmittedRef.current = null;
      return;
    }

    const processedEditorValue = getEditorValue(editor);
    if (normalizedValue !== processedEditorValue) {
      if (formatting) {
        editor.commands.setContent(normalizedValue, { emitUpdate: false });
      } else {
        editor.commands.setContent(toPlainTextContent(normalizedValue), {
          emitUpdate: false
        });
      }
      lastEmittedRef.current = null;
    }
  }, [editor, value]);

  useEffect(() => {
    return () => {
      clearTimeout(updateValueTimeout.current);
    };
  }, []);

  return (
    <div className="rich-text" ref={richTextElRef} style={wrapperStyles}>
      {editor && templateTagsIdentifier
        ? ReactDOM.createPortal(
            <ReactHint
              attribute={`data-rich-text-editor-template-tag-${templateTagsIdentifier}`}
              autoPosition={true}
              className="vuo-tooltip-alt"
              events={true}
              delay={50}
              onRenderContent={(target, content) => {
                const matchingTag = JSON.parse(
                  content
                ) as EmailMessagePlaceholder;
                return (
                  <div
                    className="vuo-tooltip__content"
                    style={{
                      borderRadius: 7,
                      fontSize: '1.5rem',
                      padding: '8px 12px'
                    }}
                  >
                    {matchingTag?.label}
                    {matchingTag?.value ? (
                      <>
                        {': '}
                        <span style={{ fontWeight: 400 }}>
                          {`"${matchingTag.value}"`}
                        </span>
                      </>
                    ) : null}
                    {matchingTag?.description ? (
                      <>
                        <br />
                        <span style={{ fontSize: '1.3rem', fontWeight: 400 }}>
                          {matchingTag.description}
                        </span>
                      </>
                    ) : null}
                  </div>
                );
              }}
            />,
            document.querySelector('.dynamic-tooltip-root')
          )
        : null}

      {editor && formatting ? (
        <RichTextEditorMenu
          colorPickerColors={colorPickerColors}
          editor={editor}
          richTextElementRef={richTextElRef}
        />
      ) : null}

      <EditorContent editor={editor} />

      {maxLength && maxLengthShowInfo ? (
        <div className="character-limit">
          {`${maxLength - editor?.storage.characterCount.characters()} ${
            messages.commonCharactersRemaining
          }`}
        </div>
      ) : maxLength &&
        maxLengthShowError &&
        editor?.storage.characterCount.characters() >= maxLength ? (
        <div className="character-limit-error">
          {`0 / ${maxLength} ${messages.commonCharactersRemaining}`}
        </div>
      ) : null}

      {error ? (
        <div
          className={`error-message ${
            withBorder
              ? 'error-message-with-border'
              : 'error-message-without-border'
          }`}
        >
          {error}
        </div>
      ) : null}

      <style jsx global>{`
        .vuo-rich-text-editor {
          background-color: white;
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColor};
          font-family: inherit;
          font-size: 1.5rem;
          font-weight: 400;
          outline: 0;
          padding: 8px 10px;
          transition: border-color 0.2s;
          width: 100%;
        }
        .vuo-rich-text-editor-with-border {
          border: 1px solid ${theme.lightGreyDark};
        }
        .vuo-rich-text-editor-with-border:focus {
          border: 1px solid #a1a8aa;
        }
        .vuo-rich-text-editor-multiline {
          min-height: ${minHeight}px;
        }
        .vuo-rich-text-editor-multiline.vuo-rich-text-editor-fixed {
          height: ${minHeight}px;
          overflow-y: auto;
        }
        .vuo-rich-text-editor-disabled {
          background-color: ${theme.lightGreyLighter};
          border: 1px solid ${theme.lightGreyDark};
          color: ${theme.textColor};
          opacity: 0.7;
        }
        .vuo-rich-text-editor p {
          margin: 0;
        }
        .vuo-rich-text-editor p:not(:first-child) {
          margin-top: 10px;
        }
        .vuo-rich-text-editor p.is-editor-empty:first-child::before {
          color: #999;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .vuo-rich-text-editor a {
          color: ${theme.linkColor};
          text-decoration: underline;
        }
        .vuo-rich-text-editor code {
          background-color: ${theme.lightGreyLighter};
          border-radius: 4px;
          font-size: 1.4rem;
          padding: 2px 4px;
        }
        .vuo-rich-text-editor .template-tag {
          background-color: ${theme.successLightColor};
          border-radius: 3px;
          font-weight: 400;
          padding: 1px;
        }
        .vuo-rich-text-editor .template-tag.suggestion + .template-tag {
          margin-left: -2px;
        }
        .vuo-rich-text-editor .template-tag-error {
          background-color: ${theme.dangerLightColor};
        }
        .vuo-rich-text-editor-with-border.vuo-rich-text-editor-error,
        .vuo-rich-text-editor-with-border.vuo-rich-text-editor-error:focus {
          border: 1px solid ${theme.dangerColor};
        }
      `}</style>

      <style jsx>{`
        .character-limit {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 400;
          padding: 3px;
        }
        .character-limit-error {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          font-weight: 400;
          padding: 3px;
        }
        .error-message {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          font-weight: 400;
          padding: 3px;
        }
        .error-message-without-border {
          padding: 0px 10px;
          position: relative;
          top: -8px;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditorInput;

/**
 * Converts a plain text string to a ProseMirror JSON document.
 * This is needed because Tiptap's `setContent()` assumes all strings are HTML,
 * which causes issues when passing plain text strings like `"<example>"`.
 * By providing Tiptap its native document format, we ensure the content is treated as text rather than HTML.
 */
const toPlainTextContent = (text: string) => ({
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: text ? [{ type: 'text', text }] : []
    }
  ]
});
